import { useMemo } from 'react'
import { Space, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { formatMoneyValue } from '@/shared/lib/money'
import { DataTable } from '@/shared/ui/table/DataTable'
import { INVOICE_STATUS_COLOR, fetchInvoicePage, formatInvoiceDay, type Invoice, type InvoiceFilter } from './invoiceApi'

interface InvoiceTableProps {
  filter: InvoiceFilter
  tableId: string
  urlPrefix?: string
  toolbarExtra?: React.ReactNode
  showCustomer?: boolean
}

export function InvoiceTable({ filter, tableId, urlPrefix, toolbarExtra, showCustomer = true }: InvoiceTableProps) {
  const { t } = useTranslation()
  const { state, subscriberId, branchId } = filter
  const stable = useMemo(() => ({ state, subscriberId, branchId }), [state, subscriberId, branchId])
  const fetchPage = useMemo(() => fetchInvoicePage(stable), [stable])

  const columns = useMemo<ColumnsType<Invoice>>(
    () => [
      {
        key: 'invoiceNo',
        title: t('invoices.columns.number'),
        width: 180,
        render: (_, row) => (
          <Link to={`/invoices/${row.id}`}>{row.invoiceNo ?? t('invoices.draftNumber')}</Link>
        ),
      },
      {
        key: 'status',
        title: t('invoices.columns.status'),
        width: 150,
        render: (_, row) => <Tag color={INVOICE_STATUS_COLOR[row.status]}>{t(`invoices.status.${row.status}`)}</Tag>,
      },
      { key: 'invoiceDate', title: t('invoices.columns.date'), width: 120, render: (_, row) => formatInvoiceDay(row.invoiceDate) },
      { key: 'dueDate', title: t('invoices.columns.due'), width: 120, render: (_, row) => formatInvoiceDay(row.dueDate) },
      ...(showCustomer
        ? [
            {
              key: 'customer',
              title: t('invoices.columns.customer'),
              width: 200,
              render: (_: unknown, row: Invoice) => (
                <Space direction="vertical" size={0}>
                  <Typography.Text>{row.customer.login}</Typography.Text>
                  {row.customer.name ? <Typography.Text type="secondary">{row.customer.name}</Typography.Text> : null}
                </Space>
              ),
            },
          ]
        : []),
      { key: 'branch', title: t('invoices.columns.branch'), width: 170, render: (_, row) => row.branch.name },
      {
        key: 'total',
        title: t('invoices.columns.total'),
        width: 140,
        align: 'right',
        render: (_, row) => formatMoneyValue(row.total),
      },
      {
        key: 'paid',
        title: t('invoices.columns.paid'),
        width: 140,
        align: 'right',
        render: (_, row) => formatMoneyValue(row.paid),
      },
      {
        key: 'outstanding',
        title: t('invoices.columns.outstanding'),
        width: 140,
        align: 'right',
        render: (_, row) =>
          row.outstanding.amount > 0 ? (
            <Typography.Text type="danger">{formatMoneyValue(row.outstanding)}</Typography.Text>
          ) : (
            formatMoneyValue(row.outstanding)
          ),
      },
    ],
    [t, showCustomer],
  )

  return (
    <DataTable<Invoice>
      queryKey={[...queryKeys.invoices.list, stable]}
      fetchPage={fetchPage}
      columns={columns}
      rowKey={(row) => row.id}
      tableId={tableId}
      {...(urlPrefix ? { urlPrefix } : {})}
      searchable
      searchPlaceholder={t('invoices.searchPlaceholder')}
      toolbarExtra={toolbarExtra}
      scrollX={1400}
      stickyFirstColumn
      showTotal={false}
      emptyTitle={t('invoices.empty')}
    />
  )
}
